import { ref } from 'vue'
import type { Ref } from 'vue'
import { is, clone } from 'ramda'
import { useCore } from '../core'
import { makeCustomEvents } from './custom-events'
import {
  NAME,
  ERROR_TIMEOUT,
  ERROR_UNKNOWN,
} from './consts'

import PERSISTENT_LISTENERS from './persistent-listeners'

import { logger } from './log'

type EventListener = {
  callback: (data: unknown) => void
  once?: boolean
}

type EventCallback = {
  resolve: (data: unknown) => void
  timeoutId: ReturnType<typeof setTimeout>
}

type SendEventTarget = {
  target?: Window
  origin?: string
}

// "persistent" listeners
const LISTENERS: Record<string, EventListener[]> = {}

// event callbacks
const CALLBACKS: Record<string, EventCallback> = {}

let ready: Ref<boolean> = ref(false)

function checkIsInitialized (): void {
  if (!ready.value) {
    throw new Error(`${NAME} not initialized`)
  }
}

/*
 * sends simple event to native application
 */
function sendEvent (name: string, data: Record<string, unknown> = {}, { target, origin }: SendEventTarget = {}): void {
  checkIsInitialized()

  const { store } = useCore()

  if (!name) {
    throw new Error('web-view event name not given')
  }

  const message = JSON.stringify({ name, data })
  /*
   * force target
   *
   * xxx: remove this functionality
   * (this service is ONLY for communication with native apps.)
   */
  if (target) {
    try {
      target.postMessage(message, origin)
      logger.debug(`message sent to given target: ${message}`)
    } catch (error) {
      logger.error(error)
      throw error
    }
    // shortcut
    return
  }

  /*
   * send event to iOS native app.
   */
  if (store.getters['app/isIOS']) {
    try {
      const win = window as Window & { webkit?: { messageHandlers: { BooksyWebViewInterface: { postMessage: (m: string) => void } } } }
      if (win.webkit) {
        win.webkit.messageHandlers.BooksyWebViewInterface.postMessage(message)
        logger.debug(`message sent to iOS native app.: ${message}`)
      } else {
        logger.debug(`window.webkit not available. web debug: Message iOS native app.: ${message}`)
      }
    } catch (error) {
      logger.error(error)
      throw error
    }
    // shortcut
    return
  }

  /*
   * send event to Android native app.
   */
  if (store.getters['app/isAndroid']) {
    try {
      (window as unknown as Window & { BooksyWebViewInterface: { postMessage: (m: string) => void } }).BooksyWebViewInterface.postMessage(message)
      logger.debug(`message sent to Android native app.: ${message}`)
    } catch (error) {
      logger.error(error)
      throw error
    }
    // shortcut
    return
  }

  // debug (development mode only)
  if (process.env.NODE_ENV === 'development') {
    logger.debug(`message sent: ${message}`)
    // shortcut
    return
  }

  const error = `no message target detected for message: ${message}`
  logger.error(error)
  throw new Error(error)
}

/*
 * send event with callback
 */
function sendEventWithCallback (name: string, data: Record<string, unknown> = {}, timeout = 20000): Promise<unknown> {
  checkIsInitialized()

  return new Promise((resolve, reject) => {
    // register callback
    CALLBACKS[name] = {
      resolve: (responseData: unknown) => {
        clearTimeout(CALLBACKS[name].timeoutId)
        delete CALLBACKS[name]
        resolve(responseData)
      },
      timeoutId: setTimeout(() => {
        delete CALLBACKS[name]
        logger.error(`event "${name}" timed out`)
        reject(new Error(`${ERROR_TIMEOUT}: event "${name}"`))
      }, timeout),
    }

    try {
      sendEvent(name, data)
    } catch (error) {
      clearTimeout(CALLBACKS[name].timeoutId)
      delete CALLBACKS[name]
      reject(error)
    }
  })
}

/*
 * add event listener
 */
function addEventListener (name: string, callback: (data: unknown) => void, options: { once?: boolean } = { once: false }): void {
  if (!name) {
    throw new Error('Event name not given')
  }

  if (!callback) {
    throw new Error('Event callback not given')
  }

  if (typeof callback !== 'function') {
    throw new Error('Event callback must be a function')
  }

  const listener: EventListener = {
    ...options,
    callback,
  }

  if (Array.isArray(LISTENERS[name])) {
    LISTENERS[name].push(listener)
  } else {
    LISTENERS[name] = [listener]
  }
}

/*
 * remove event listener
 */
function removeEventListener (name: string, callback: (data: unknown) => void): void {
  if (!name) {
    throw new Error('Event name not given')
  }

  if (!callback) {
    throw new Error('Event callback not given')
  }

  if (typeof callback !== 'function') {
    throw new Error('Event callback must be a function')
  }

  // shortcut
  if (!LISTENERS[name]) return

  // shortcut
  if (!Array.isArray(LISTENERS[name])) return

  // remove listener from list
  LISTENERS[name] = LISTENERS[name].filter(listener => listener.callback !== callback)

  // delete list if empty
  if (!LISTENERS[name].length) {
    delete LISTENERS[name]
  }
}

/*
 * handle message event
 */
function handleMessageEvent (event: MessageEvent | CustomEvent): void {
  const eventDetail = clone((event as CustomEvent).detail ?? (event as MessageEvent).data)

  try {
    const detail = (
      is(Object, eventDetail)
        ? eventDetail
        : (
          is(String, eventDetail)
            ? JSON.parse(eventDetail as string)
            : null
        )
    ) as { name?: string; data?: unknown } | null || {}

    if (!is(Object, detail) || !is(String, detail.name) || !is(Object, detail.data)) {
      // shortcut: ignore
      return
    }

    const { name, data } = detail

    if (!LISTENERS[name] && !CALLBACKS[name]) {
      logger.debug(`received unexpected event "${name}", skipping...`)
      // shortcut
      return
    }

    logger.debug(`received "${name}" event with data:`, data)

    if (LISTENERS[name]) {
      for (const listener of LISTENERS[name]) {
        try {
          listener.callback(data)
          if (listener.once) {
            removeEventListener(name, listener.callback)
          }
        } catch (error) {
          logger.error(error)
        }
      }
    }

    if (CALLBACKS[name]?.resolve) {
      CALLBACKS[name].resolve(data)
    }
  } catch (error) {
    logger.error(error, eventDetail)
  }
}

/*
 * adds persistent event listeners
 */
function addPersistentListeners (): void {
  const registered: string[] = []

  // register persistent listeners
  PERSISTENT_LISTENERS.forEach(({ name, callback }: { name: string; callback: (data: unknown) => void }) => {
    // shortcut: bad name
    if (!is(String, name)) {
      logger.error('persistent listener name not set or not a string')
      return
    }

    // shortcut: bad callback
    if (typeof callback !== 'function') {
      logger.error('persistent listener callback not set or not a function')
      return
    }

    addEventListener(name, callback)
    registered.push(name)
  })

  logger.debug('registered persistent listeners:', registered)
}

/*
 * removes persistent listeners
 */
function removePersistentListeners (): void {
  const removed: string[] = []

  // register persistent listeners
  PERSISTENT_LISTENERS.forEach(({ name, callback }: { name: string; callback: (data: unknown) => void }) => {
    if (is(String, name) && (typeof callback === 'function')) {
      removeEventListener(name, callback)
      removed.push(name)
    }
  })

  logger.debug('removed persistent listeners:', removed)
}

/*
 * init
 */
export function init (): void {
  document.addEventListener('BooksyWebViewMessage', handleMessageEvent as (ev: Event) => void)

  addPersistentListeners()

  // xxx: remove this functionality
  // (this service is ONLY for communication with native apps.)
  window.addEventListener('message', handleMessageEvent as (ev: Event) => void)

  ready.value = true
}

/*
 * destroy
 */
export function destroy (): void {
  document.removeEventListener('BooksyWebViewMessage', handleMessageEvent as (ev: Event) => void)

  // xxx: remove this functionality
  // (this service is ONLY for communication with native apps.)
  window.removeEventListener('message', handleMessageEvent as (ev: Event) => void)

  removePersistentListeners()

  ready.value = false
}

export type UseWebViewEventBusReturn = {
  events: ReturnType<typeof makeCustomEvents>
  ready: Ref<boolean>
  init: () => void
  destroy: () => void
  sendEvent: (name: string, data?: Record<string, unknown>, options?: SendEventTarget) => void
  sendEventWithCallback: (name: string, data?: Record<string, unknown>, timeout?: number) => Promise<unknown>
  addEventListener: (name: string, callback: (data: unknown) => void, options?: { once?: boolean }) => void
  removeEventListener: (name: string, callback: (data: unknown) => void) => void
}

export function useWebViewEventBus (): UseWebViewEventBusReturn {
  return {
    /* custom events, eg.:
     *   sendEhlo
     *   sendClearWebViewCache
     *   ...
     */
    events: makeCustomEvents({
      sendEvent,
      sendEventWithCallback,
      ERROR_TIMEOUT,
      ERROR_UNKNOWN,
    }),

    ready,

    init,
    destroy,

    sendEvent,
    sendEventWithCallback,
    addEventListener,
    removeEventListener,
  }
}
